import { useEffect, useRef, useState } from "react";
import { getConnection } from "../utils/signalRConnection";

const MAX_SIGNALR_RETRIES = 10;

const useSignalR = () => {
	const [connection, setConnection] = useState(null);
	const retryCountRef = useRef(0);
	const retryTimerRef = useRef(null);
	const onConnectedCallbacks = useRef([]);

	const onConnected = (cb) => {
		onConnectedCallbacks.current.push(cb);
		if (connection?.state === "Connected") {
			cb(connection);
		}
	};

	// Handler registry: [{ type, group, callback }]
	const handlers = useRef([]);

	// Register business-logic handlers
	const registerHandler = (type, group, callback) => {
		const handler = { type, group, callback };
		handlers.current.push(handler);

		return () => {
			handlers.current = handlers.current.filter(h => h !== handler);
		};
	};

	useEffect(() => {
		const conn = getConnection();
		setConnection(conn);

		const startConnection = async () => {
			try {
				await conn.start();
				retryCountRef.current = 0;
				console.debug("SignalR connected:", window.env.signalRHubUrl);
				onConnectedCallbacks.current.forEach(cb => cb(conn));
			}
			catch (err) {
				++retryCountRef.current;
				if (retryCountRef.current > MAX_SIGNALR_RETRIES) {
					console.error(`SignalR connection failed after ${MAX_SIGNALR_RETRIES} attempts`, err);
					return;
				}

				const delay = Math.min(30000, 2000 * (2 ** (retryCountRef.current - 1)));
				console.warn(`SignalR reconnect attempt ${retryCountRef.current} failed — retrying in ${delay / 1000}s`);

				clearTimeout(retryTimerRef.current);
				retryTimerRef.current = setTimeout(startConnection, delay);
			}
		};

		conn.onclose(() => {
			startConnection();
		});

		startConnection();

		return () => {
			clearTimeout(retryTimerRef.current);
		};
	}, []);

	// Unified message dispatcher
	useEffect(() => {
		if (!connection) {
			return;
		}

		connection.off("ReceiveMessageAsync");

		const onMessage = (message) => {
			console.debug("Recieved Message from SignalR server...", message);
			handlers.current.forEach((h) => {
				if (h.type === message.type && h.group === message.group) {
					h.callback(message);
				}
			});
		};

		connection.on("ReceiveMessageAsync", onMessage);

		return () => {
			connection.off("ReceiveMessageAsync", onMessage);
		};
	}, [connection]);

	return {
		connection,
		onConnected,
		registerHandler
	};
};

export default useSignalR;